import { useEffect, useState, useCallback, useMemo } from "react";
import api, { fmtIDR, fileUrl } from "@/lib/api";
import { PageHeader, Loading, Empty } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Search, ScanLine } from "lucide-react";
import BarcodeScanner from "@/components/BarcodeScanner";

// columns: [{key,label,money,render}]  fields: [{name,label,type,options,money,required}]
export default function CrudResource({
  title, subtitle, endpoint, columns, fields, searchable = true,
  canCreate = true, canEdit = true, canDelete = true, transform, totalField, scanSearch = false, rowActions, facets, sorts,
}) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [scan, setScan] = useState(null);
  const [facetVal, setFacetVal] = useState({});
  const [sortVal, setSortVal] = useState("");

  const displayRows = useMemo(() => {
    let r = [...rows];
    (facets || []).forEach((f) => {
      const v = facetVal[f.key];
      if (v && v !== "all") r = r.filter((row) => (f.match ? f.match(row, v) : String(row[f.key] ?? "") === v));
    });
    if (sortVal) { const s = (sorts || []).find((x) => x.value === sortVal); if (s) r = r.sort(s.fn); }
    return r;
  }, [rows, facetVal, sortVal, facets, sorts]);

  const autoOpts = (key) => Array.from(new Set(rows.map((r) => r[key]).filter(Boolean))).map((v) => ({ value: v, label: v }));

  const uploadImage = async (e, name) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    setUploading(true);
    try {
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const url = data?.url || data?.path || data?.file_url || data;
      setForm((prev) => ({ ...prev, [name]: url }));
      toast.success("Foto diunggah");
    } catch (err) {
      toast.error("Gagal upload foto");
    } finally {
      setUploading(false);
    }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(endpoint, { params: q && searchable ? { q } : {} });
      setRows(Array.isArray(data) ? data : data.items || []);
    } catch (e) {
      toast.error("Gagal memuat data");
    } finally { setLoading(false); }
  }, [endpoint, q, searchable]);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => {
    const init = {};
    fields.forEach((f) => (init[f.name] = f.type === "number" ? 0 : f.default ?? ""));
    setForm(init); setEditing(null); setOpen(true);
  };
  const openEdit = (row) => {
    const init = {};
    fields.forEach((f) => {
      const rawValue = row[f.name];
      const fallback = f.type === "number" ? 0 : (f.default ?? "");
      init[f.name] = rawValue === "" || rawValue == null ? fallback : rawValue;
    });
    setForm(init); setEditing(row); setOpen(true);
  };

  const save = async () => {
    for (const f of fields)
      if (f.required && (form[f.name] === "" || form[f.name] == null))
        return toast.error(`${f.label} wajib diisi`);
    setSaving(true);
    try {
      let payload = { ...form };
      fields.forEach((f) => { if (f.type === "number") payload[f.name] = Number(payload[f.name] || 0); });
      if (transform) payload = transform(payload, editing);
      if (editing) await api.put(`${endpoint}/${editing.id}`, payload);
      else await api.post(endpoint, payload);
      toast.success(editing ? "Berhasil diperbarui" : "Berhasil ditambahkan");
      setOpen(false); load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal menyimpan");
    } finally { setSaving(false); }
  };

  const del = async (row) => {
    if (!window.confirm("Hapus data ini?")) return;
    try { await api.delete(`${endpoint}/${row.id}`); toast.success("Dihapus"); load(); }
    catch { toast.error("Gagal menghapus"); }
  };

  return (
    <div>
      <PageHeader title={title} subtitle={subtitle}>
        {canCreate && (
          <Button onClick={openCreate} data-testid="crud-add-btn" className="bg-sky-600 hover:bg-sky-700">
            <Plus className="w-4 h-4 mr-1.5" /> Tambah
          </Button>
        )}
      </PageHeader>

      {searchable && (
        <div className="mb-4 flex gap-2 max-w-sm">
          <div className="relative flex-1">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cari..." className="pl-9" data-testid="crud-search" />
          </div>
          {scanSearch && (
            <Button type="button" variant="outline" onClick={() => setScan({ search: true })} data-testid="crud-scan-btn">
              <ScanLine className="w-4 h-4" />
            </Button>
          )}
        </div>
      )}

      {(facets || sorts) && (
        <div className="mb-4 flex flex-wrap gap-2 items-end">
          {(facets || []).map((f) => (
            <div key={f.key}>
              <label className="text-[11px] text-slate-500 block mb-1">{f.label}</label>
              <Select value={facetVal[f.key] || "all"} onValueChange={(v) => setFacetVal({ ...facetVal, [f.key]: v })}>
                <SelectTrigger className="h-9 w-44" data-testid={`facet-${f.key}`}><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Semua</SelectItem>
                  {(f.auto ? autoOpts(f.key) : (f.options || [])).filter((o) => (o.value ?? o) !== "all").map((o) => (
                    <SelectItem key={o.value ?? o} value={String(o.value ?? o)}>{o.label ?? o}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          ))}
          {sorts && (
            <div>
              <label className="text-[11px] text-slate-500 block mb-1">Urutkan</label>
              <Select value={sortVal || "none"} onValueChange={(v) => setSortVal(v === "none" ? "" : v)}>
                <SelectTrigger className="h-9 w-52" data-testid="sort-select"><SelectValue placeholder="Default" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Default</SelectItem>
                  {sorts.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>
      )}

      {totalField && (
        <div className="mb-4 bg-white rounded-xl border border-slate-200 shadow-sm p-4 inline-flex items-center gap-3" data-testid="crud-total">
          <span className="text-xs uppercase tracking-wide font-medium text-slate-500">{totalField.label || "Total"}</span>
          <span className="text-xl font-semibold font-mono-num text-sky-700">
            {fmtIDR(rows.reduce((s, r) => s + Number(r[totalField.key] || 0), 0))}
          </span>
          <span className="text-xs text-slate-400">({rows.length} data)</span>
        </div>
      )}

      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                {columns.map((c) => (
                  <th key={c.key} className={`px-4 py-3 font-medium text-slate-500 ${c.money ? "text-right" : "text-left"}`}>{c.label}</th>
                ))}
                {(canEdit || canDelete || rowActions) && <th className="px-4 py-3 text-right font-medium text-slate-500">Aksi</th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading ? (
                <tr><td colSpan={columns.length + 1}><Loading /></td></tr>
              ) : displayRows.length === 0 ? (
                <tr><td colSpan={columns.length + 1}><Empty /></td></tr>
              ) : displayRows.map((row) => (
                <tr key={row.id} className="hover:bg-slate-50 transition-colors">
                  {columns.map((c) => (
                    <td key={c.key} className={`px-4 py-3 text-slate-700 ${c.money ? "text-right font-mono-num" : ""}`}>
                      {c.render ? c.render(row) : c.money ? fmtIDR(row[c.key]) : (row[c.key] ?? "-")}
                    </td>
                  ))}
                  {(canEdit || canDelete || rowActions) && (
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      {rowActions && rowActions(row)}
                      {canEdit && (
                        <button onClick={() => openEdit(row)} data-testid={`edit-${row.id}`} className="p-1.5 text-slate-500 hover:text-sky-600">
                          <Pencil className="w-4 h-4" />
                        </button>
                      )}
                      {canDelete && (
                        <button onClick={() => del(row)} data-testid={`delete-${row.id}`} className="p-1.5 text-slate-500 hover:text-red-600">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg w-full">
          <div className="max-h-[90vh] w-full flex flex-col">
            <DialogHeader className="flex-shrink-0 px-4 py-3"><DialogTitle>{editing ? "Edit" : "Tambah"} {title}</DialogTitle></DialogHeader>
            <div className="flex-1 overflow-y-auto px-4 py-3">
              <div className="grid grid-cols-2 gap-4">
                {fields.map((f) => (
                  <div key={f.name} className={f.full ? "col-span-2" : "col-span-1"}>
                    <Label className="text-xs text-slate-500">{f.label}{f.required && " *"}</Label>
                    {f.type === "select" ? (
                      <Select
                        value={form[f.name] === "" || form[f.name] == null ? String(f.default ?? "all") : String(form[f.name])}
                        onValueChange={(v) => setForm({ ...form, [f.name]: v })}
                      >
                        <SelectTrigger className="mt-1" data-testid={`field-${f.name}`}><SelectValue placeholder="Pilih" /></SelectTrigger>
                        <SelectContent>
                          {f.options.map((o) => (
                            <SelectItem key={o.value ?? o} value={String(o.value ?? o)}>{o.label ?? o}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : f.type === "image" ? (
                      <div className="mt-1 flex items-center gap-3">
                        {form[f.name]
                          ? <img src={fileUrl(form[f.name])} alt="" className="w-16 h-16 rounded-lg object-cover border border-slate-200" />
                          : <div className="w-16 h-16 rounded-lg bg-slate-100 flex items-center justify-center text-slate-300 text-[10px]">No Img</div>}
                        <input type="file" accept="image/*" onChange={(e) => uploadImage(e, f.name)} data-testid={`field-${f.name}`} className="text-xs" />
                        {uploading && <span className="text-xs text-slate-400">Mengupload...</span>}
                      </div>
                    ) : f.scannable ? (
                      <div className="mt-1 flex gap-2">
                        <Input type="text" value={form[f.name] ?? ""} onChange={(e) => setForm({ ...form, [f.name]: e.target.value })} data-testid={`field-${f.name}`} />
                        <Button type="button" variant="outline" className="shrink-0" onClick={() => setScan({ field: f.name })} data-testid={`scan-${f.name}`}>
                          <ScanLine className="w-4 h-4" />
                        </Button>
                      </div>
                    ) : (
                      <Input
                        type={f.type === "number" ? "number" : f.type === "date" ? "date" : "text"}
                        value={form[f.name] ?? ""}
                        onChange={(e) => setForm({ ...form, [f.name]: e.target.value })}
                        className="mt-1" data-testid={`field-${f.name}`}
                      />
                    )}
                  </div>
                ))}
              </div>
            </div>
            <div className="flex-shrink-0 border-t bg-white px-4 py-3" style={{ paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 12px)' }}>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setOpen(false)}>Batal</Button>
                <Button onClick={save} disabled={saving} className="bg-sky-600 hover:bg-sky-700" data-testid="crud-save-btn">
                  {saving ? "Menyimpan..." : "Simpan"}
                </Button>
              </div>
            </div>
          </div>
      </DialogContent>
      </Dialog>

      <BarcodeScanner open={!!scan} onClose={() => setScan(null)} onScan={(code) => {
        if (scan?.search) setQ(code);
        else if (scan?.field) setForm((f) => ({ ...f, [scan.field]: code }));
        setScan(null);
        toast.success("Kode terbaca: " + code);
      }} />
    </div>
  );
}
